import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Filter, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { formatDisplayDate } from '../../utils/dateUtils';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;

const BookingOperations = () => {
    const [bookings, setBookings] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchBookings();
        fetchMetrics();
    }, [statusFilter]);

    const fetchBookings = async () => {
        try {
            const url = statusFilter === 'all' ? `${API}/bookings` : `${API}/bookings?status=${statusFilter}`;
            const response = await axios.get(url);
            setBookings(response.data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
        }
    };

    const fetchMetrics = async () => {
        try {
            const response = await axios.get(`${API}/bookings/metrics`);
            setMetrics(response.data);
        } catch (error) {
            console.error('Error fetching booking metrics:', error);
        } finally {
            setLoading(false);
        }
    };

    const statusOptions = ['all', 'Confirmed', 'Pending', 'Cancelled'];

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 animate-pulse">
                    <div className="h-48 bg-slate-200 dark:bg-slate-800 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
            data-testid="booking-operations"
        >
            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Total Bookings</div>
                    <div className="text-3xl font-bold text-slate-900 dark:text-white">{metrics?.total_bookings || 0}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Confirmed</div>
                    <div className="text-3xl font-bold text-green-500">{metrics?.confirmed || 0}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Pending</div>
                    <div className="text-3xl font-bold text-orange-500">{metrics?.pending || 0}</div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">Conversion Rate</div>
                    <div className="text-3xl font-bold text-blue-500">{metrics?.conversion_rate || 0}%</div>
                </div>
            </div>

            {/* AI Insights Banner */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold mb-2">Booking Intelligence</h3>
                        <p className="text-white/90">
                            Shanghai → Rotterdam route has <span className="font-bold">18% cancellation risk</span> due to port congestion.
                            Consider alternative ports or earlier booking.
                        </p>
                    </div>
                </div>
            </div>

            {/* Bookings Table */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Recent Bookings</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage all your bookings</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Filter className="w-5 h-5 text-slate-400" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            data-testid="booking-status-filter"
                            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {statusOptions?.map((status) => (
                                <option key={status} value={status}>
                                    {status === 'all' ? 'All Bookings' : status}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full" data-testid="bookings-table">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Booking ID</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Customer</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Route</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Carrier</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Vessel</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Date</th>
                                <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookings?.map((booking, index) => (
                                <tr
                                    key={booking.id}
                                    data-testid={`booking-row-${index}`}
                                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <td className="py-4 px-4 text-sm font-medium text-slate-900 dark:text-white">
                                        #{booking.id.substring(0, 8)}
                                    </td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">{booking.customer}</td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">
                                        {booking.origin} → {booking.destination}
                                    </td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">{booking.carrier}</td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">{booking.vessel_name}</td>
                                    <td className="py-4 px-4 text-sm text-slate-600 dark:text-slate-400">
                                        {formatDisplayDate(booking.booking_date)}
                                    </td>
                                    <td className="py-4 px-4 text-center">
                                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${booking.status === 'Confirmed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                            booking.status === 'Pending' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                                                'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                            }`}>
                                            {booking.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </motion.div>
    );
};

export default BookingOperations;